if (!customElements.get('free-shipping-progress')) {
  class FreeShippingProgress extends HTMLElement {
    connectedCallback() {
      this.messageElement = this.querySelector('[data-free-shipping-message]');
      this.barElement = this.querySelector('[data-free-shipping-bar]');
      this.trackElement = this.querySelector('[role="progressbar"]');
      this.refresh = this.refresh.bind(this);
      this.refresh();

      const notification = this.closest('cart-notification')?.querySelector('#cart-notification');
      if (notification) {
        this.notificationObserver = new MutationObserver(() => {
          if (notification.classList.contains('active')) this.refresh();
        });
        this.notificationObserver.observe(notification, { attributes: true, attributeFilter: ['class'] });
      }
    }

    disconnectedCallback() {
      if (this.notificationObserver) this.notificationObserver.disconnect();
    }

    async refresh() {
      if (this.isLoading) return;
      this.isLoading = true;

      try {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const response = await fetch(`${cartUrl}.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Unable to load cart');
        const cart = await response.json();
        this.renderProgress(cart);
      } catch (error) {
        this.hidden = true;
      } finally {
        this.isLoading = false;
      }
    }

    renderProgress(cart) {
      if (!cart || cart.item_count === 0) {
        this.hidden = true;
        return;
      }

      const country = this.dataset.country;
      const shopCurrency = this.dataset.shopCurrency;
      const currency = cart.currency || this.dataset.currency || shopCurrency;
      const baseThreshold = Number(
        country === 'AU'
          ? this.dataset.auThreshold
          : country === 'US'
            ? this.dataset.usThreshold
            : this.dataset.internationalThreshold
      );

      let threshold = baseThreshold;
      if (currency !== shopCurrency) {
        const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
        if (!Number.isFinite(rate) || rate <= 0) {
          this.hidden = true;
          return;
        }
        threshold = Math.round(baseThreshold * rate);
      }

      const total = Number(cart.total_price);
      const remaining = Math.max(threshold - total, 0);
      const progress = Math.min(Math.max((total / threshold) * 100, 0), 100);

      if (remaining > 0) {
        const amount = this.formatMoney(remaining, currency);
        if (country === 'AU') {
          this.messageElement.textContent = `You’re ${amount} away from free shipping.`;
        } else if (country === 'US') {
          this.messageElement.textContent = `You’re ${amount} away from free standard DDP shipping.`;
        } else {
          this.messageElement.textContent = `You’re ${amount} away from free standard international shipping.`;
        }
      } else if (country === 'AU') {
        this.messageElement.textContent = 'You’ve unlocked free standard shipping!';
      } else if (country === 'US') {
        this.messageElement.textContent = 'You’ve unlocked free standard DDP shipping!';
      } else {
        this.messageElement.textContent = 'You’ve unlocked free standard international shipping!';
      }

      this.barElement.style.width = `${progress}%`;
      this.trackElement.setAttribute('aria-valuenow', String(Math.round(progress)));
      this.hidden = false;
    }

    formatMoney(cents, currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(cents / 100);
      } catch (error) {
        return `${currency} ${(cents / 100).toFixed(2)}`;
      }
    }
  }

  customElements.define('free-shipping-progress', FreeShippingProgress);
}